(() => {
  'use strict';

  const supportedLocales = new Set(['en', 'hi', 'mr', 'kn', 'ml', 'ta', 'te', 'gu']);
  const documentLocale = (document.documentElement.lang || '').toLowerCase().split('-')[0];
  const shopifyRoot = window.Shopify?.routes?.root || '/';
  const rootLocale = shopifyRoot.split('/').filter(Boolean)[0]?.toLowerCase();
  const activeLocale = supportedLocales.has(documentLocale)
    ? documentLocale
    : supportedLocales.has(rootLocale)
      ? rootLocale
      : 'en';
  const activePrefix = activeLocale === 'en' ? '' : `/${activeLocale}`;
  const localeAtStart = /^\/(en|hi|mr|kn|ml|ta|te|gu)(?=\/|$)/i;
  const nonPagePath = /^\/(?:admin|checkouts?|services|apps|challenge|tools|cdn|\.well-known)(?:\/|$)/i;
  const filePath = /\.(?:avif|bmp|css|csv|docx?|gif|ico|jpe?g|js|json|map|mp3|mp4|pdf|png|svg|txt|webm|webp|woff2?|xml|zip)$/i;

  const stripLocale = (pathname) => {
    const stripped = pathname.replace(localeAtStart, '');
    return stripped === '' ? '/' : stripped;
  };

  const localizeUrl = (rawUrl) => {
    if (
      !rawUrl ||
      rawUrl.startsWith('#') ||
      /^(?:mailto:|tel:|sms:|javascript:|data:|blob:)/i.test(rawUrl)
    ) {
      return rawUrl;
    }

    let url;
    try {
      url = new URL(rawUrl, window.location.origin);
    } catch (_error) {
      return rawUrl;
    }

    if (url.origin !== window.location.origin) return rawUrl;

    const storefrontPath = stripLocale(url.pathname);
    if (nonPagePath.test(storefrontPath) || filePath.test(storefrontPath)) return rawUrl;

    const localizedPath = `${activePrefix}${storefrontPath === '/' ? '/' : storefrontPath}`;
    return `${localizedPath}${url.search}${url.hash}`;
  };

  const shouldSkip = (element) =>
    element.hasAttribute('hreflang') ||
    element.hasAttribute('download') ||
    element.hasAttribute('data-rg-no-localize') ||
    Boolean(element.closest('localization-form, .localization-form, [data-localization-form]'));

  const localizeLink = (link) => {
    if (shouldSkip(link)) return;
    const currentHref = link.getAttribute('href');
    const localizedHref = localizeUrl(currentHref);
    if (localizedHref && localizedHref !== currentHref) link.setAttribute('href', localizedHref);
  };

  const localizeForm = (form) => {
    if (shouldSkip(form) || (form.method || 'get').toLowerCase() !== 'get') return;
    const currentAction = form.getAttribute('action');
    if (!currentAction) return;
    const localizedAction = localizeUrl(currentAction);
    if (localizedAction && localizedAction !== currentAction) form.setAttribute('action', localizedAction);
  };

  const localizeElement = (element) => {
    if (element.matches?.('a[href]')) localizeLink(element);
    if (element.matches?.('form[action]')) localizeForm(element);
  };

  const localizeWithin = (root) => {
    if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_NODE) return;
    localizeElement(root);
    root.querySelectorAll?.('a[href], form[action]').forEach(localizeElement);
  };

  const start = () => {
    localizeWithin(document);

    document.addEventListener(
      'click',
      (event) => {
        const link = event.target.closest?.('a[href]');
        if (link) localizeLink(link);
      },
      true
    );

    document.addEventListener(
      'submit',
      (event) => {
        if (event.target.matches?.('form[action]')) localizeForm(event.target);
      },
      true
    );

    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes') {
          localizeElement(mutation.target);
          return;
        }
        mutation.addedNodes.forEach(localizeWithin);
      });
    }).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['href', 'action'],
    });
  };

  window.RudraGramLocaleTools = {
    activeLocale,
    localizeUrl,
    stripLocale,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
